import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Clock, MapPin } from 'lucide-react';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import { getResponsiveImageSizes } from '../../../utils/imageUtils';
import type { Package } from '../../../lib/types/api';

interface PackageCardProps {
  package: Package;
}

const PackageCard: React.FC<PackageCardProps> = React.memo(({ package: pkg }) => {
  // Sanitize and truncate description
  const sanitizedDescription = pkg.summary || pkg.description
    ? DOMPurify.sanitize(pkg.summary || pkg.description || '')
    : '';

  // Extract conversion triggers and legacy flags
  const rawTriggers: string[] = Array.isArray(pkg.conversion_triggers)
    ? pkg.conversion_triggers.filter(t => typeof t === 'string' && t.trim().length > 0)
    : [];

  const triggersList = [...rawTriggers];
  if (pkg.is_deal && !triggersList.some(t => /deal|sale|discount/i.test(t))) {
    triggersList.unshift('Hot Deal');
  }
  if (pkg.is_featured && !triggersList.some(t => /featured/i.test(t))) {
    triggersList.push('Featured');
  }

  const primaryTrigger = triggersList[0];
  const secondaryTrigger = triggersList.length > 1 ? triggersList[1] : null;

  return (
    <article className="group bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.02] flex flex-col h-full">
      <Link
        to={`/packages/${pkg.country?.slug || 'unknown'}/${pkg.slug}/`}
        className="flex flex-col h-full min-h-[44px]"
        aria-label={`View details for ${pkg.name} package`}
      >
        {/* Image */}
        <div className="relative w-full h-40 sm:h-48 md:h-52 overflow-hidden bg-gray-200">
          {pkg.image_id ? (
            <OptimizedImage
              imageId={pkg.image_id}
              alt={`${pkg.name} tour package in ${pkg.country?.name || 'destination'}`}
              variant="medium"
              className="w-full h-full transition-transform duration-300 group-hover:scale-110"
              objectFit="cover"
              loading="lazy"
              sizes={getResponsiveImageSizes('card')}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-teal-400 to-primary flex items-center justify-center" role="img" aria-label="Default package image">
              <MapPin className="w-12 h-12 text-white opacity-50" aria-hidden="true" />
            </div>
          )}

          {/* Primary Badge Overlay (Top Left) */}
          {primaryTrigger && (
            <div
              className={`absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full shadow-md z-10 flex items-center gap-1 ${
                /deal|sale|discount/i.test(primaryTrigger)
                  ? 'bg-red-600 text-white'
                  : /featured|popular|bestseller|best seller/i.test(primaryTrigger)
                    ? 'bg-amber-400 text-gray-900 font-bold'
                    : 'bg-teal text-white font-bold'
              }`}
              role="status"
            >
              {/deal|sale|discount/i.test(primaryTrigger) && <span className="animate-pulse">🔥</span>}
              {/featured|popular|bestseller|best seller/i.test(primaryTrigger) && <span>⭐</span>}
              {!/deal|sale|discount|featured|popular|bestseller|best seller/i.test(primaryTrigger) && <span>⚡</span>}
              {primaryTrigger}
            </div>
          )}

          {/* Secondary Badge Overlay (Top Right) */}
          {secondaryTrigger && (
            <div
              className={`absolute top-3 right-3 text-xs font-bold px-3 py-1 rounded-full shadow-md z-10 flex items-center gap-1 ${
                /featured|popular|bestseller|best seller/i.test(secondaryTrigger)
                  ? 'bg-amber-400 text-gray-900 font-bold'
                  : 'bg-charcoal/80 text-white backdrop-blur-xs font-semibold'
              }`}
              role="status"
            >
              {secondaryTrigger}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-grow">
          {/* Destinations & Holiday Types */}
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Primary destination */}
              {pkg.country && (
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-700 border border-teal-100">
                  <MapPin className="w-3.5 h-3.5 mr-1 text-teal-600 flex-shrink-0" />
                  <span className="uppercase tracking-wide">{pkg.country.name}</span>
                </div>
              )}
              {/* Additional destinations */}
              {pkg.countries && pkg.countries.length > 0 && (
                <>
                  {pkg.countries.slice(0, 2).map((c) => (
                    <div key={c.id} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-50 text-gray-600 border border-gray-200">
                      <MapPin className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                      <span className="uppercase tracking-wide">{c.name}</span>
                    </div>
                  ))}
                  {pkg.countries.length > 2 && (
                    <span className="text-xs text-gray-400 font-medium">+{pkg.countries.length - 2} more</span>
                  )}
                </>
              )}
            </div>
            {pkg.holiday_types && pkg.holiday_types.length > 0 && (
              <span className="inline-block bg-gray-50 text-gray-600 text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0 border border-gray-200">
                {pkg.holiday_types[0].name}
              </span>
            )}
          </div>

          {/* Conversion Triggers Pills (In-Card Display) */}
          {rawTriggers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {rawTriggers.map((trigger, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 bg-amber-50 text-amber-900 border border-amber-200/80 text-[11px] font-bold px-2.5 py-0.5 rounded-md uppercase tracking-wider shadow-2xs"
                >
                  <span className="text-amber-500">⚡</span>
                  {trigger}
                </span>
              ))}
            </div>
          )}

          {/* Package Name - H3 for proper heading hierarchy */}
          <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-600 transition-colors leading-tight font-playfair">
            {pkg.name}
          </h3>

          {/* Description */}
          {sanitizedDescription && (
            <div
              className="text-sm text-gray-600 mb-4 line-clamp-2 flex-grow leading-relaxed"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
            />
          )}

          {/* Footer - Duration and Price */}
          <div className="flex items-end justify-between pt-4 mt-auto border-t border-gray-100">
            <div className="flex items-center bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-100 shadow-sm">
              <Clock className="w-4 h-4 mr-1.5 text-orange-600 flex-shrink-0" />
              <span className="text-sm font-bold text-orange-800">{pkg.duration_days} Days</span>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-0.5">Starting From</div>
              <div className="text-2xl font-bold text-teal-600 leading-none tracking-tight">
                ${pkg.price.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Rating (if available) */}
          {pkg.rating && pkg.rating > 0 && (
            <div className="flex items-center mt-2 pt-2 border-t border-gray-100">
              <div className="flex text-yellow-400" role="img" aria-label={`Rating: ${pkg.rating.toFixed(1)} out of 5 stars`}>
                {[...Array(5)].map((_, i) => (
                  <svg
                    key={i}
                    className={`w-4 h-4 ${i < Math.floor(pkg.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <span className="ml-2 text-xs text-gray-600">
                {pkg.rating.toFixed(1)} ({pkg.review_count || 0} reviews)
              </span>
            </div>
          )}
        </div>
      </Link>
    </article>
  );
});

PackageCard.displayName = 'PackageCard';

export default PackageCard;
